"use client";

import React from "react";
import { BentoGrid, Grid, GridItem } from "../DynamicHighlights.styles";
import { bentoSpans, lastRowOffset, lastRowStartIndex, type BentoSpan } from "../bentoLayout";
import type { HighlightItem } from "../types";

export type HighlightsGridProps = {
  items: HighlightItem[];
  arrangement: "uniform" | "bento";
  columns: number;
  gap: number;
  renderItem: (item: HighlightItem, span: BentoSpan) => React.ReactNode;
};

export const HighlightsGrid = ({
  items,
  arrangement,
  columns,
  gap,
  renderItem,
}: HighlightsGridProps) => {
  if (arrangement === "bento") {
    /*
     * A composição é escolhida pela QUANTIDADE (ver `bentoLayout.ts`), não por
     * um padrão fixo que se repete — era isso que deixava buraco quando a
     * quantidade não era múltipla do padrão.
     */
    const spans = bentoSpans(items.length);

    return (
      <BentoGrid $gap={gap} data-testid="dynamic-highlights-grid">
        {items.map((item, index) => {
          const span = spans[index] || "minor";
          return (
            <GridItem key={item.id} $span={span}>
              {renderItem(item, span)}
            </GridItem>
          );
        })}
      </BentoGrid>
    );
  }

  /*
   * Última linha incompleta fica centralizada: o primeiro item dela recebe um
   * `grid-column-start` deslocado, e os seguintes fluem ao lado. Só o primeiro
   * precisa da marca.
   */
  const offset = lastRowOffset({ total: items.length, columns });
  const offsetIndex = lastRowStartIndex({ total: items.length, columns });

  return (
    <Grid $cols={columns} $gap={gap} data-testid="dynamic-highlights-grid">
      {items.map((item, index) => (
        <GridItem
          key={item.id}
          $colStart={index === offsetIndex ? offset ?? undefined : undefined}
        >
          {renderItem(item, "minor")}
        </GridItem>
      ))}
    </Grid>
  );
};
